/**
 * Privacy mode: cover the figures that are nobody else's business.
 *
 * A switch the owner throws before handing the phone over or sharing a screen,
 * and throws back afterwards. Nothing about it is automatic.
 *
 * It used to be: on by default at every launch, turned off by Face ID, and
 * turned back on by a timer. That guards against forgetting, and the cost is
 * that it guards against you as well — the app opened covered every single
 * time, and the one thing anybody actually does with a finance app is look at
 * their own numbers. A control that fights its owner on every launch to protect
 * them during the rare minute they are showing somebody else is the wrong way
 * round.
 *
 * So the mode is now off, stays off, and stays on once switched on. A mode that
 * expires would be back to guessing when the conversation ended.
 */

/** Stands in for a figure while privacy mode is on. */
export const MASK = '••••';

const KEY = 'fintrack.privacyMode';

/**
 * Whether privacy mode was left on.
 *
 * Remembered across launches, which is the difference between a mode and a
 * gesture: a screen still being shared when the app is reopened must not
 * uncover itself, and nothing else knows the sharing has stopped.
 *
 * Storage can throw outright — Safari in private browsing, a browser set to
 * block site data — so a failure to read is treated as "not on". Defaulting the
 * other way would leave someone unable to see their own figures on a device
 * where the answer can never be stored.
 */
export function readPrivacyMode(): boolean {
  try {
    return localStorage.getItem(KEY) === 'on';
  } catch {
    return false;
  }
}

/**
 * Whether a change to the mode is allowed to go ahead.
 *
 * The rule in one place, because it is asymmetric and the asymmetry is the
 * point: covering your own figures needs no permission, and uncovering them
 * has to prove who is holding the phone. Without the second half the mode
 * protects nothing — anyone handed the phone can uncover it from the same
 * screen. Without the first, hiding something in a hurry means waiting on a
 * Face ID prompt, which is the moment you least want one.
 *
 * `authenticate` is passed in rather than reached for so this stays a rule
 * about the mode rather than a dependency on how a device proves identity, and
 * so a test can watch whether it is called at all.
 */
export function authorizePrivacyChange(
  on: boolean,
  authenticate: () => Promise<boolean>,
): Promise<boolean> {
  if (on) return Promise.resolve(true);
  return authenticate();
}

/** Remembers the switch. Silent on failure: the mode still works this session. */
export function writePrivacyMode(on: boolean): void {
  try {
    if (on) localStorage.setItem(KEY, 'on');
    else localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
